<template>
  <div class="expected-work-days-form">
    <ValidationObserver ref="observer">
      <div
        v-if="limitsByShiftsEnabled"
        class="row"
      >
        <div class="col-lg-6 col-sm-6 col-xs-12">
          <div
            id="limit-by-shifts"
            class="form-group mb-10 mt-5"
          >
            <ValidationProvider>
              <div class="checkbox">
                <AppOnOffToggle
                  v-model="limitByShiftsLocal"
                  title="When this setting is enabled, the duration of the scheduled shifts will be applied as the daily limit"
                >
                  <span>Use shifts to set work allowance limits</span>
                </AppOnOffToggle>
              </div>
            </ValidationProvider>
            <section
              v-if="limitByShifts"
              class="manage-shifts-block"
            >
              <section class="progress-section">
                <template v-if="limitByShifts">
                  <div
                    v-if="totalDailyLimitByShifts"
                    id="shifts"
                  >
                    <div class="progress-details">
                      <div class="tracked-wrapper">
                        <span class="tracked">
                          {{ formatLimitDuration(trackedDailyLimitByShifts) }}
                        </span>
                        <span class="progress-text-divider"></span>
                        <span class="total">
                          {{ formatLimitDuration(totalDailyLimitByShifts) }}
                        </span>
                      </div>
                      <a
                        class="progress-action"
                        :href="shiftsLink"
                      >
                        Manage shifts
                      </a>
                    </div>
                    <AppProgressBar
                      :tracked="trackedDailyLimitByShifts"
                      :total="totalDailyLimitByShifts"
                    />
                  </div>
                  <div
                    v-else
                    id="shifts"
                  >
                    <div class="progress-details">
                      <div class="mr-5">
                        <span class="text-lighter"> No shifts </span>
                      </div>
                      <a
                        class="progress-action"
                        :href="shiftsLink"
                      >
                        Create a shift
                      </a>
                    </div>
                    <AppProgressBar
                      tracked="0"
                      total="100"
                    />
                  </div>
                </template>
              </section>
            </section>
          </div>
        </div>
      </div>
      <div class="row mt-30">
        <div class="col-lg-6 col-sm-6 col-xs-12">
          <div>
            <label class="control-label"> Weekly work days </label>
            <p>
              Set the week days members are expected to work. You can also set which days members are not allowed to
              track time.
            </p>
            <div class="expected-work-days-form-container">
              <div
                v-if="limitByShifts"
                v-tooltip="tooltipOptions"
                class="expected-work-days-form-overlay allowed-expected-days"
                :title="tooltips.settingIsDisabled"
              ></div>
              <div class="mt-20">
                <ValidationProvider
                  ref="dowExpected"
                  v-slot="{ errors, invalid }"
                  name="dowExpected"
                  rules="required"
                >
                  <div
                    id="dow-limit"
                    class="form-group"
                    :class="{ 'has-error': invalid || errors[0] }"
                  >
                    <span class="expected-days-label">
                      Expected work days:
                      <span class="strong capitalize">{{ expectedWorkDaysString }}</span>
                    </span>
                    <div class="input-group mt-10">
                      <div class="btn-group-spaced weekdays">
                        <div
                          v-for="{ value, text } in sortedWeekdays"
                          :key="value"
                        >
                          <label
                            :for="value"
                            class="ml-10 p-0"
                          >
                            <input
                              :id="value"
                              v-model="dowExpectedLocal"
                              type="checkbox"
                              :value="text"
                              class="dow-limit-input"
                              :disabled="isDowExpectedDisabled(text)"
                            />
                            <a
                              class="btn btn-sm btn-default btn-circle dow-limits-btn ml-0"
                              :class="toggleDowExpectedBtnClass(text)"
                              @click="$refs.dowExpected.setFlags({ dirty: true })"
                            >
                              {{ text | capitalize }}
                            </a>
                            <i
                              v-show="disableOnSpecificDays"
                              class="fa"
                              :class="toggleDowLimitClass(text)"
                              @click.prevent="setDowLimit(text)"
                            ></i>
                          </label>
                        </div>
                      </div>
                    </div>
                    <span
                      v-if="invalid || errors[0]"
                      class="help-block mt-20"
                    >
                      Must select at least one day
                    </span>
                  </div>
                </ValidationProvider>
              </div>
              <div
                id="specific-days"
                class="form-group mt-30 mb-10"
              >
                <ValidationProvider>
                  <div class="checkbox">
                    <AppOnOffToggle v-model="disableOnSpecificDays">
                      <span>Disable time tracking on specific days</span>
                    </AppOnOffToggle>
                  </div>
                </ValidationProvider>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-6 col-sm-12 col-xs-12">
          <div class="col-lg-12">
            <ValidationProvider
              v-slot="{ errors }"
              ref="expected_per_week"
              name="expected_per_week"
              :rules="`greaterThanZero|expectedPerWeekLocalRule:@weekly_limit|max_value:${MAX_WEEKLY_LIMIT}`"
            >
              <div
                class="form-group mb-5"
                :class="{ 'has-error': errors[0] }"
              >
                <label class="control-label">
                  <div class="d-flex align-items-center">Expected weekly work hours</div>
                </label>
                <div class="mb-5"><small>Set the hours members are expected to work weekly</small></div>
                <div class="input-group">
                  <AppInput
                    v-model="expectedPerWeekLocal"
                    type="number"
                    placeholder="No expected hours"
                  />
                  <span class="input-group-addon">hrs/wk</span>
                </div>
                <div
                  v-if="errors[0]"
                  class="mb-20"
                >
                  <span class="help-block">{{ errors[0] }}</span>
                </div>
                <div
                  v-else
                  class="remove-wrapper"
                >
                  <small class="help-block has-justified-children">
                    <span></span>
                    <span v-if="expectedPerWeekLocal">
                      <a
                        class="remove"
                        @click="removeExpectedWeekly"
                      >
                        Remove
                      </a>
                    </span>
                  </small>
                </div>
              </div>
            </ValidationProvider>

            <ValidationProvider
              v-slot="{ errors }"
              ref="weekly_limit"
              name="weekly_limit"
              :rules="`greaterThanZero|max_value:${MAX_WEEKLY_LIMIT}`"
            >
              <div class="expected-work-days-form-container mt-20">
                <div
                  id="recurring-weekly-limit"
                  class="form-group mb-5"
                  :class="{ 'has-error': errors[0] }"
                >
                  <label class="control-label">
                    <div class="d-flex align-items-center">
                      Weekly limit
                      <i
                        v-if="weeklyLimitLocal"
                        v-tooltip="tooltipOptions"
                        class="hi hi-info-filled hi-12 a-tooltip ml-5"
                        :title="tooltips.weekly"
                      ></i>
                    </div>
                  </label>
                  <div class="mb-5"><small>Set the hours members are allowed to work weekly</small></div>
                  <div class="input-group-wrapper">
                    <div class="input-group">
                      <AppInput
                        v-model="weeklyLimitLocal"
                        type="number"
                        placeholder="No weekly limit"
                      />
                      <span class="input-group-addon">hrs/wk</span>
                    </div>
                    <section
                      v-if="totalWeeklyLimit"
                      class="manage-limits-block"
                    >
                      <section>
                        <div v-if="errors[0]">
                          <span class="help-block">{{ errors[0] }}</span>
                        </div>
                        <div
                          v-else
                          class="remove-wrapper"
                        >
                          <small class="help-block has-justified-children">
                            <div class="value">{{ weeklyLimitStatus }}</div>
                            <span v-if="weeklyLimitLocal">
                              <a
                                class="remove"
                                @click="removeWeekly"
                              >
                                Remove
                              </a>
                            </span>
                          </small>
                        </div>
                      </section>
                      <section class="progress-section">
                        <label class="control-label">
                          <div class="d-flex align-items-center">THIS WEEK</div>
                        </label>
                        <div class="progress-details">
                          <div class="tracked-wrapper">
                            <span class="tracked">
                              {{ formatLimitDuration(trackedWeeklyLimit) }}
                            </span>
                            <span class="progress-text-divider"></span>
                            <span :class="{ 'limit-changed': currentWeeklyLimitChanged }">
                              {{ formatLimitDuration(totalWeeklyLimit) }}
                            </span>
                          </div>
                          <MemberDialogTabLimitsPopover
                            limit-type="weekly"
                            @limit-change="onCurrentLimitChange"
                          >
                            <template #trigger>
                              <a class="progress-action">Edit limit</a>
                            </template>
                          </MemberDialogTabLimitsPopover>
                        </div>
                        <AppProgressBar
                          :tracked="trackedWeeklyLimit"
                          :total="totalWeeklyLimit"
                        />
                      </section>
                    </section>
                  </div>
                </div>
              </div>
            </ValidationProvider>
            <div class="expected-work-days-form-container mt-32">
              <div
                v-if="limitByShifts"
                v-tooltip="tooltipOptions"
                class="expected-work-days-form-overlay expected-work-days-form-overlay-shifts"
                :title="tooltips.settingIsDisabled"
              ></div>
              <div id="recurring-daily-limit">
                <label class="control-label">
                  <div class="d-flex align-items-center">
                    Daily limit
                    <i
                      v-if="dailyLimitLocal"
                      v-tooltip="tooltipOptions"
                      class="hi hi-info-filled hi-12 a-tooltip ml-5"
                      :title="tooltips.daily"
                    ></i>
                  </div>
                </label>
                <div class="mb-5"><small>Set the hours members are allowed to work daily</small></div>
                <div>
                  <ValidationProvider
                    v-slot="{ errors }"
                    ref="daily_limit"
                    name="daily_limit"
                    :rules="`greaterThanZero|max_value:${MAX_DAILY_LIMIT}`"
                  >
                    <div
                      id="daily-limit"
                      class="form-group"
                      :class="{ 'has-error': errors[0] }"
                    >
                      <div class="input-group-wrapper">
                        <div class="input-group">
                          <AppInput
                            v-model="dailyLimitLocal"
                            type="number"
                            placeholder="No daily limit"
                          />
                          <span class="input-group-addon">hrs/day</span>
                        </div>
                        <section
                          v-if="!limitByShifts && totalDailyLimit"
                          class="manage-limits-block"
                        >
                          <section class="progress-section">
                            <div
                              v-if="errors[0]"
                              class="mb-20"
                            >
                              <span class="help-block">{{ errors[0] }}</span>
                            </div>
                            <div
                              v-else
                              class="remove-wrapper"
                            >
                              <small class="help-block has-justified-children">
                                <div class="value">{{ dailyLimitStatus }}</div>
                                <span v-if="dailyLimit">
                                  <a
                                    class="remove"
                                    @click="removeDaily"
                                  >
                                    Remove
                                  </a>
                                </span>
                              </small>
                            </div>
                            <label class="control-label">
                              <div class="d-flex align-items-center">TODAY</div>
                            </label>
                            <div>
                              <div class="progress-details">
                                <div class="tracked-wrapper">
                                  <span class="tracked">
                                    {{ formatLimitDuration(trackedDailyLimit) }}
                                  </span>
                                  <span class="progress-text-divider"></span>
                                  <span :class="{ 'limit-changed': currentDailyLimitChanged }">
                                    {{ formatLimitDuration(totalDailyLimit) }}
                                  </span>
                                </div>
                                <MemberDialogTabLimitsPopover
                                  limit-type="daily"
                                  @limit-change="onCurrentLimitChange"
                                >
                                  <template #trigger>
                                    <a class="progress-action">Edit limit</a>
                                  </template>
                                </MemberDialogTabLimitsPopover>
                              </div>
                              <AppProgressBar
                                :tracked="trackedDailyLimit"
                                :total="totalDailyLimit"
                              />
                            </div>
                          </section>
                        </section>
                      </div>
                    </div>
                  </ValidationProvider>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import { extend } from 'vee-validate';
import { MAX_DAILY_LIMIT, MAX_WEEKLY_LIMIT, EXPECTED_WORK_DAYS } from '~/app_constants';
import { daysOfWork } from '~/utils/string_utils';
import { formatDuration } from '~/utils/time_utils';
import MemberDialogTabLimitsPopover from '~/components/members/MemberDialogTabLimitsPopover.vue';

extend('expectedPerWeekLocalRule', {
  params: ['target'],
  validate(value, { target }) {
    if (target) {
      return value <= target;
    }

    return true;
  },
  message: 'must be less than or equal to weekly limit',
});

const weekDays = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export default {
  components: {
    MemberDialogTabLimitsPopover,
  },
  props: {
    limitsByShiftsEnabled: {
      type: Boolean,
      default: true,
    },
    limitByShifts: {
      type: Boolean,
      required: true,
    },
    sortedWeekdays: {
      type: Array,
      required: true,
    },
    dowExpected: {
      type: Array,
      required: true,
    },
    dowLimit: {
      type: Array,
      required: true,
    },
    dailyLimit: {
      type: [Number, String],
      validator: (prop) => typeof prop === 'number' || typeof prop === 'string' || prop === null,
    },
    weeklyLimit: {
      type: [String, Number],
      default: null,
      validator: (prop) =>
        typeof prop === 'number' || typeof prop === 'string' || prop === null || typeof prop === 'number',
    },
    expectedPerWeek: {
      type: [String, Number],
      default: null,
      validator: (prop) => typeof prop === 'string' || prop === null || typeof prop === 'number',
    },
    weeklyLimitStatus: {
      type: String,
      required: true,
    },
    dailyLimitStatus: {
      type: String,
      required: true,
    },
    trackedWeeklyLimit: {
      type: Number,
      required: true,
    },
    trackedDailyLimit: {
      type: Number,
      required: true,
    },
    totalDailyLimit: {
      type: Number,
      required: true,
    },
    totalWeeklyLimit: {
      type: Number,
      required: true,
    },
    totalDailyLimitByShifts: {
      type: Number,
      required: true,
    },
    trackedDailyLimitByShifts: {
      type: Number,
      required: true,
    },
    shiftsLink: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    tooltipOptions: Object.freeze({
      placement: 'top',
    }),
    disableOnSpecificDays: false,
    currentDailyLimitChanged: false,
    currentWeeklyLimitChanged: false,
    MAX_DAILY_LIMIT,
    MAX_WEEKLY_LIMIT,
    tooltips: EXPECTED_WORK_DAYS.tooltips,
  }),
  computed: {
    // TODO: Implement the `modelValue` approach when we have a moment
    limitByShiftsLocal: {
      get() {
        return this.limitByShifts;
      },
      set(val) {
        this.$emit('setLimitsByShifts', val);
      },
    },
    expectedPerWeekLocal: {
      get() {
        return this.expectedPerWeek;
      },
      set(val) {
        this.$emit('setExpectedPerWeek', val);
      },
    },
    weeklyLimitLocal: {
      get() {
        return this.weeklyLimit;
      },
      set(val) {
        this.$emit('setWeeklyLimit', val);
      },
    },
    dailyLimitLocal: {
      get() {
        return this.dailyLimit;
      },
      set(val) {
        this.$emit('setDailyLimit', val);
      },
    },
    dowExpectedLocal: {
      get() {
        return this.dowExpected;
      },
      set(val) {
        this.$emit('setDowExpected', val);
      },
    },
    expectedWorkDaysString() {
      return daysOfWork(this.dowExpectedLocal);
    },
  },
  watch: {
    disableOnSpecificDays(newValue) {
      const daysToSet = weekDays.filter((day) => !this.dowLimit.includes(day));

      if (!newValue) {
        daysToSet.forEach((day) => {
          this.setDowLimit(day);
        });
      }
    },
  },
  created() {
    if (this.dowLimit.length < 7) {
      this.disableOnSpecificDays = true;
    }
  },
  methods: {
    formatLimitDuration(limit) {
      return formatDuration(limit, { showSeconds: 'never' });
    },
    validate() {
      return this.$refs.observer.validate().then((valid) => {
        return valid;
      });
    },
    isDowExpectedDisabled(day) {
      return !this.dowLimit.includes(day);
    },
    toggleDowExpectedBtnClass(day) {
      if (!this.dowExpectedLocal || !day) {
        return;
      }

      let classes = '';

      if (this.disableOnSpecificDays && !this.dowLimit.includes(day)) {
        classes += ' danger';
      }

      if (this.dowExpectedLocal.includes(day)) {
        classes += ' active';
      }

      return classes;
    },
    toggleDowLimitClass(day) {
      if (!this.dowLimit || !day) {
        return;
      }

      return this.dowLimit.includes(day) ? 'fa-unlock' : 'fa-lock';
    },
    setDowLimit(day) {
      const event = this.dowLimit.includes(day) ? 'remove' : 'add';

      if (event === 'remove' && this.dowExpected.includes(day)) {
        this.dowExpectedLocal = this.dowExpected.filter((item) => item !== day);
      }

      this.$emit('setDowLimit', { day, event });
    },
    removeExpectedWeekly() {
      this.$refs.expected_per_week.setFlags({ dirty: true });

      this.$emit('removeExpectedPerWeek');
    },
    removeWeekly() {
      this.$refs.weekly_limit.setFlags({ dirty: true });

      this.$emit('removeRecurringWeeklyLimit');
    },
    removeDaily() {
      this.$refs.daily_limit.setFlags({ dirty: true });

      this.$emit('removeRecurringDailyLimit');
    },
    onCurrentLimitChange(type) {
      if (type === 'weekly') {
        this.currentWeeklyLimitChanged = true;
      } else {
        this.currentDailyLimitChanged = true;
      }
    },
  },
};
</script>
